import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircleIcon, StarIcon } from '@heroicons/react/24/outline';
import { apiService } from '../services/api';
import { InternetPlan } from '../types';
import AdvancedRouterModal from '../components/AdvancedRouterModal';
import LocalCitySeoBlock from '../components/LocalCitySeoBlock';
import MobilePlanCarousel from '../components/MobilePlanCarousel';
import PlanConnectModal from '../components/PlanConnectModal';
import SlimWaveDivider from '../components/SlimWaveDivider';
import { LocalCitySeo } from '../data/localSeoContent';
import { LeadContext, buildCheckServiceUrl, clearLeadContext } from '../utils/leadContext';

export type PlansPageProps = {
  city?: string;
  heroTitle?: string;
  heroSubtitle?: string;
  localSeo?: LocalCitySeo;
};

const Plans: React.FC<PlansPageProps> = ({
  city,
  heroTitle = 'Internet Plans for Every Need',
  heroSubtitle =
    "Whether it's for your home or business, we've got you covered. Choose the perfect plan with flexible pricing and no hidden fees.",
  localSeo,
}) => {
  const [plans, setPlans] = useState<InternetPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [customerType, setCustomerType] = useState<'residential' | 'business'>('residential');
  const [routerModalOpen, setRouterModalOpen] = useState(false);
  const [lead, setLead] = useState<LeadContext | null>(null);
  const [connectPlan, setConnectPlan] = useState<InternetPlan | null>(null);

  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const st = location.state as { leadContext?: LeadContext; fromHomeCheck?: boolean } | null;
    // Only keep an address from this visit's homepage check — never an old saved one
    if (st?.fromHomeCheck && st?.leadContext) {
      setLead(st.leadContext);
    } else {
      clearLeadContext();
      setLead(null);
    }
  }, [location.state, location.pathname]);

  const handleGetConnected = (plan: InternetPlan) => {
    // Already verified on homepage → skip address popup, go straight to contact
    if (lead?.available && lead.lat && lead.lng && lead.address) {
      const url = buildCheckServiceUrl({
        plan: { id: plan.id, name: plan.name, price: plan.price, speed: plan.speed },
        lead,
      });
      navigate(url, {
        state: {
          selectedPlan: plan.id,
          selectedPlanName: plan.name,
          selectedPlanPrice: plan.price,
          selectedPlanSpeed: plan.speed,
          leadContext: lead,
        },
      });
      window.scrollTo(0, 0);
      return;
    }
    // Landed on plans directly (Google, nav, etc.) → ask for address once
    setConnectPlan(plan);
  };
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const plansData = await apiService.getPlans();
        setPlans(plansData);
      } catch (error) {
        console.error('Error fetching plans:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPlans();
  }, []);

  // Scroll to hash anchor (e.g. #wave-fiber) after plans load
  useEffect(() => {
    if (!loading && location.hash) {
      const el = document.querySelector(location.hash);
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: 'smooth' }), 100);
      }
    }
  }, [loading, location.hash]);

  const getRouterRentalDisplay = (plan: InternetPlan) => {
    if (plan.name === 'STARTER') return 'N/A';
    if (plan.category === 'business' || plan.name === 'Business PRO') return 'Included';
    return '$10.00/month';
  };

  const getPlanFeatures = (plan: InternetPlan) => plan.features;

  const getDownloadSpeed = (plan: InternetPlan) => {
    if (plan.speed.includes('/')) {
      let download = plan.speed.split('/')[0].trim();
      if (!/mbps|gbps|\bg\b/i.test(download)) {
        download = `${download} Mbps`;
      }
      return download;
    }
    return plan.speed;
  };

  const getUploadSpeed = (plan: InternetPlan) => {
    if (!plan.speed.includes('/')) return plan.speed;

    const parts = plan.speed.split('/');
    if (parts.length < 2) return plan.speed;

    let upload = parts.slice(1).join('/').trim();
    if (!/mbps|gbps|\bg\b/i.test(upload)) {
      upload = `${upload} Mbps`;
    }
    return upload;
  };

  // Kingman: no LTU / Standard package grid — keep STARTER only with home WAVE plans.
  const isKingmanPricing = city === 'Kingman';
  const isBusinessPlan = (plan: InternetPlan) =>
    plan.category === 'business' || plan.name === 'Business PRO';
  const starterPlan = plans.find((plan) => plan.name === 'STARTER');
  const basicPlans = isKingmanPricing
    ? []
    : plans.filter((plan) => plan.category === 'basic');
  const fiberPlans = isKingmanPricing
    ? [
        ...(starterPlan ? [starterPlan] : []),
        ...plans.filter((plan) => plan.category === 'fiber' && !isBusinessPlan(plan)),
      ]
    : plans.filter((plan) => plan.category === 'fiber' && !isBusinessPlan(plan));
  const businessPlans = plans
    .filter((plan) => isBusinessPlan(plan))
    .sort((a, b) => a.price - b.price);
  const activeAudiencePlans = customerType === 'business' ? businessPlans : fiberPlans;
  const visiblePlans = isKingmanPricing
    ? activeAudiencePlans
    : [...basicPlans, ...fiberPlans, ...businessPlans];

  const renderPlanCard = (plan: InternetPlan, index: number, variant: 'wave' | 'business') => {
    const isBusiness = variant === 'business';
    const ctaClass = isBusiness
      ? 'bg-white text-slate-900 hover:bg-white/90'
      : 'bg-blue-600 text-white hover:bg-blue-500';
    const speedIconBg = isBusiness ? 'bg-white/10' : 'bg-blue-500/15';
    const speedIconColor = isBusiness ? 'text-white' : 'text-blue-300';

    return (
      <motion.div
        key={plan.id}
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: index * 0.1 }}
        className={`bg-[#0a0f1a] rounded-2xl border border-white/10 shadow-lg shadow-black/30 card-hover relative mobile-plan-card ${
          plan.popular ? 'ring-2 ring-blue-500 scale-105' : ''
        }`}
      >
        {plan.popular && (
          <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 z-10 popular-badge">
            <span className="bg-blue-600 text-white px-4 py-1 rounded-full text-sm font-semibold flex items-center">
              <StarIcon className="h-4 w-4 mr-1" />
              Most Popular
            </span>
          </div>
        )}

        <div className="p-8">
          <div className="text-center mb-6">
            <h3 className="text-2xl font-bold text-white mb-2">{plan.name}</h3>
            <div className="text-4xl font-bold text-blue-400 mb-2 mobile-plan-price">
              ${plan.price}
              <span className="text-lg text-white/50">/month</span>
            </div>
            {plan.taxesApply && (
              <p className="text-sm text-white/45 mt-1">+ tax</p>
            )}
            <div className="mt-4 flex items-center justify-center gap-5">
              <div className="text-center">
                <div className={`w-10 h-10 rounded-full ${speedIconBg} flex items-center justify-center mx-auto mb-1`}>
                  <svg className={`w-5 h-5 ${speedIconColor}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                  </svg>
                </div>
                <p className="text-xs text-white/45">Download</p>
                <p className={`text-base font-bold ${speedIconColor}`}>{getDownloadSpeed(plan)}</p>
              </div>
              <div className="text-center">
                <div className={`w-10 h-10 rounded-full ${speedIconBg} flex items-center justify-center mx-auto mb-1`}>
                  <svg className={`w-5 h-5 ${speedIconColor}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 10l7-7m0 0l7 7m-7-7v18" />
                  </svg>
                </div>
                <p className="text-xs text-white/45">Upload</p>
                <p className={`text-base font-bold ${speedIconColor}`}>{getUploadSpeed(plan)}</p>
              </div>
            </div>
          </div>

          <ul className="hidden md:block space-y-3 mb-4 mobile-plan-features">
            {getPlanFeatures(plan).filter((f) => !f.includes('(Optional Add-on)')).map((feature, featureIndex) => (
              <li key={featureIndex} className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-400 mr-2 mt-0.5 flex-shrink-0" />
                <span className="text-white/65 text-sm">{feature}</span>
              </li>
            ))}
          </ul>

          {getPlanFeatures(plan).filter((f) => f.includes('(Optional Add-on)')).length > 0 && (
            <div className="hidden md:block mb-8">
              <div className="border-t border-white/10 my-3"></div>
              <p className="text-xs font-bold text-white/45 uppercase tracking-wider mb-2">Optional Add-ons</p>
              <ul className="space-y-2">
                {getPlanFeatures(plan).filter((f) => f.includes('(Optional Add-on)')).map((feature, featureIndex) => (
                  <li key={featureIndex} className="flex items-start">
                    <span className="text-blue-400 mr-2 mt-0.5 flex-shrink-0 text-sm">+</span>
                    <span className="text-white/55 text-sm">{feature.replace(' (Optional Add-on)', '')}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="space-y-3 mt-6 md:mt-0">
            <button
              type="button"
              onClick={() => handleGetConnected(plan)}
              className={`w-full text-center block py-3 px-4 rounded-lg font-semibold transition-all ${ctaClass}`}
            >
              Get Connected
            </button>
          </div>
        </div>
      </motion.div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0a0f1a]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-400"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0f1a] pt-20">
      {/* Hero Section */}
      <section className="relative">
        {city ? (
          <>
            {/* Mobile city hero — mountain banner matching design */}
            <div className="md:hidden relative overflow-hidden bg-gradient-to-b from-[#3b7cf0] via-[#1e56d8] to-[#163fa8]">
              <div className="relative z-10 px-4 pt-12 pb-20 text-center">
                <motion.h1
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.55 }}
                  className="leading-[0.95]"
                >
                  <span className="block text-[2.85rem] font-extrabold tracking-tight text-white drop-shadow-sm">
                    {city}
                  </span>
                  <span className="mt-1 block text-[2.85rem] font-extrabold tracking-tight text-[#5ec8ff]">
                    Plans
                  </span>
                </motion.h1>
              </div>
              <svg
                className="pointer-events-none absolute inset-x-0 bottom-0 w-full h-[88px]"
                viewBox="0 0 390 88"
                preserveAspectRatio="none"
                aria-hidden="true"
              >
                <path
                  fill="#0a0f1a"
                  d="M0 88V52L28 44L58 58L92 28L128 50L158 36L198 62L238 22L278 48L312 34L348 56L372 42L390 50V88H0Z"
                />
                <path
                  fill="#070b14"
                  opacity="0.55"
                  d="M0 88V64L42 58L78 70L118 52L162 68L210 48L256 66L304 54L350 68L390 60V88H0Z"
                />
              </svg>
            </div>

            {/* Desktop city hero — matched Golden Valley / Kingman layout */}
            <div className="hidden md:block bg-gradient-to-br from-airmax-blue to-blue-600 py-12 md:py-16">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8 }}
                >
                  <h1 className="font-bold text-white text-3xl md:text-5xl lg:text-6xl mb-4 md:mb-6">
                    {heroTitle}
                  </h1>
                  {heroSubtitle && (
                    <p className="text-lg md:text-2xl text-blue-100 max-w-3xl mx-auto">
                      {heroSubtitle}
                    </p>
                  )}
                </motion.div>
              </div>
            </div>
          </>
        ) : (
          <div
            className={`bg-gradient-to-br from-airmax-blue to-blue-600 ${
              isKingmanPricing ? 'py-8 md:py-10' : 'py-12 md:py-16'
            }`}
          >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
              >
                <h1
                  className={`font-bold text-white ${
                    isKingmanPricing
                      ? 'text-3xl md:text-4xl lg:text-5xl'
                      : 'text-3xl md:text-5xl lg:text-6xl mb-4 md:mb-6'
                  }`}
                >
                  {heroTitle}
                </h1>
                {!isKingmanPricing && heroSubtitle && (
                  <p className="text-lg md:text-2xl text-blue-100 max-w-3xl mx-auto mt-4 md:mt-6">
                    {heroSubtitle}
                  </p>
                )}
              </motion.div>
            </div>
          </div>
        )}
      </section>

      {lead?.available && (
        <div className="bg-green-500/15 border-b border-green-400/30">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 text-sm text-green-200">
            <strong>Service available</strong> at {lead.formatted || `${lead.address}, ${lead.city}`}
            {' — '}pick a plan below, then leave your contact info.
          </div>
        </div>
      )}

      {!city && (
        <section className="py-8 md:py-10 bg-[#111827] border-b border-white/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-xl md:text-2xl font-bold text-white mb-2">Shop by city</h2>
            <p className="text-white/60 mb-3 max-w-3xl text-sm md:text-base">
              Dedicated Kingman and Golden Valley internet pages with the same Airmax plans.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/internet-kingman-az"
                onClick={() => window.scrollTo(0, 0)}
                className="text-blue-400 font-semibold hover:underline"
              >
                Internet in Kingman, AZ →
              </Link>
              <Link
                to="/internet-golden-valley-az"
                onClick={() => window.scrollTo(0, 0)}
                className="text-blue-400 font-semibold hover:underline"
              >
                Internet in Golden Valley, AZ →
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Coverage intro — skipped on Kingman (go straight to prices) */}
      {!isKingmanPricing && (
      <section className="hidden md:block py-8 md:py-10 bg-[#0a0f1a] border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-center text-sm md:text-base text-white/65 mb-6 max-w-3xl mx-auto">
            These are <strong className="text-white">two different networks</strong> — not two versions of the same plan.
            WAVE is only available at select addresses. Check your address to see which coverage you can get.
          </p>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="rounded-xl border border-blue-400/30 bg-blue-500/10 p-5">
              <p className="text-xs font-bold uppercase tracking-wider text-blue-400 mb-1">Most addresses</p>
              <h2 className="text-xl font-bold text-white">Airmax Standard Coverage</h2>
              <p className="mt-2 text-sm text-white/65">
                Available throughout more of our service area. Reliable fixed wireless internet with speeds up to 200+ Mbps.
              </p>
            </div>
            <div className="rounded-xl border border-green-400/30 bg-green-500/10 p-5">
              <p className="text-xs font-bold uppercase tracking-wider text-green-400 mb-1">Select WAVE areas only</p>
              <h2 className="text-xl font-bold text-white">Airmax WAVE — Next-Generation Coverage</h2>
              <p className="mt-2 text-sm text-white/65">
                Lightning-fast Fiber Speeds up to 1 Gig — built for streaming, gaming, remote work, and growing businesses.
              </p>
            </div>
          </div>
        </div>
      </section>
      )}

      {/* Airmax Standard Coverage (hidden for Kingman — LTU packages removed) */}
      {basicPlans.length > 0 && (
      <section className={`${city ? 'pt-2 pb-10' : 'py-10'} md:py-20 bg-[#111827]`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`text-center mb-6 md:mb-16 ${city ? 'hidden md:block' : ''}`}>
            <p className="text-sm font-bold uppercase tracking-wider text-blue-400 mb-2">Most of our service area</p>
            <h2 className="text-2xl md:text-4xl font-bold text-white mb-2 md:mb-4">
              Airmax Standard Coverage
            </h2>
            <p className="hidden md:block text-xl text-white/65 max-w-3xl mx-auto mb-3">
              Available throughout more of our service area
            </p>
            <p className="hidden md:block text-white/60 max-w-3xl mx-auto mb-6">
              Reliable fixed wireless internet with speeds up to 200+ Mbps. This is the network most Kingman and Golden Valley addresses can get — not a slower version of WAVE.
            </p>
            <p className="md:hidden text-sm text-white/55">
              Swipe packages · Fixed wireless
            </p>
          </div>

          <MobilePlanCarousel
            plans={basicPlans}
            variant="standard"
            getDownloadSpeed={getDownloadSpeed}
            getUploadSpeed={getUploadSpeed}
            onGetConnected={handleGetConnected}
          />

          <div className="hidden md:grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mt-8">
            {basicPlans.map((plan, index) => (
              <motion.div
                key={plan.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className={`bg-[#0a0f1a] rounded-2xl border border-white/10 shadow-lg shadow-black/30 card-hover relative mobile-plan-card ${
                  plan.popular ? 'ring-2 ring-blue-500 scale-105' : ''
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 z-10 popular-badge">
                    <span className="bg-blue-600 text-white px-4 py-1 rounded-full text-sm font-semibold flex items-center">
                      <StarIcon className="h-4 w-4 mr-1" />
                      Most Popular
                    </span>
                  </div>
                )}
                
                <div className="p-8">
                  {/* Technology Badge */}
                  <div className="text-center mb-3">
                    <span className="bg-blue-600 text-white px-3 py-1 rounded-full text-xs font-bold">
                      STANDARD COVERAGE
                    </span>
                    <p className="text-xs text-white/45 italic mt-2">Fixed wireless · most addresses</p>
                  </div>
                  
                  <div className="text-center mb-6">
                    <h3 className="text-2xl font-bold text-white mb-2">{plan.name}</h3>
                    <div className="text-4xl font-bold text-blue-400 mb-2 mobile-plan-price">
                      ${plan.price}
                      <span className="text-lg text-white/50">/month</span>
                    </div>
                    {plan.taxesApply && (
                      <p className="text-sm text-white/45 mt-1">+ tax</p>
                    )}
                    <div className="mt-4 flex items-center justify-center gap-5">
                      <div className="text-center">
                        <div className="w-10 h-10 rounded-full bg-blue-500/15 flex items-center justify-center mx-auto mb-1">
                          <svg className="w-5 h-5 text-blue-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                          </svg>
                        </div>
                        <p className="text-xs text-white/45">Download</p>
                        <p className="text-base font-bold text-blue-300">{getDownloadSpeed(plan)}</p>
                      </div>
                      <div className="text-center">
                        <div className="w-10 h-10 rounded-full bg-green-500/15 flex items-center justify-center mx-auto mb-1">
                          <svg className="w-5 h-5 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 10l7-7m0 0l7 7m-7-7v18" />
                          </svg>
                        </div>
                        <p className="text-xs text-white/45">Upload</p>
                        <p className="text-base font-bold text-green-400">{getUploadSpeed(plan)}</p>
                      </div>
                    </div>
                  </div>

                  <ul className="space-y-3 mb-4 mobile-plan-features">
                    {getPlanFeatures(plan).filter(f => !f.includes('(Optional Add-on)')).map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-start">
                        <CheckCircleIcon className="h-5 w-5 text-green-400 mr-2 mt-0.5 flex-shrink-0" />
                        <span className="text-white/65 text-sm">
                          {feature}
                        </span>
                      </li>
                    ))}
                  </ul>

                  {getPlanFeatures(plan).filter(f => f.includes('(Optional Add-on)')).length > 0 && (
                    <div className="mb-8">
                      <div className="border-t border-white/10 my-3"></div>
                      <p className="text-xs font-bold text-white/45 uppercase tracking-wider mb-2">Optional Add-ons</p>
                      <ul className="space-y-2">
                        {getPlanFeatures(plan).filter(f => f.includes('(Optional Add-on)')).map((feature, featureIndex) => (
                          <li key={featureIndex} className="flex items-start">
                            <span className="text-blue-400 mr-2 mt-0.5 flex-shrink-0 text-sm">+</span>
                            <span className="text-white/55 text-sm">{feature.replace(' (Optional Add-on)', '')}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <button
                    type="button"
                    onClick={() => handleGetConnected(plan)}
                    className="w-full text-center block py-3 px-4 rounded-lg font-semibold transition-all bg-blue-600 text-white hover:bg-blue-500"
                  >
                    Get Connected
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      )}

      {basicPlans.length > 0 && (
      <SlimWaveDivider className="bg-[#0a0f1a]" />
      )}

      {/* WAVE packages — Residential / Business + billing toggles */}
      <section
        id="wave-fiber"
        className={`bg-[#0a0f1a] ${
          isKingmanPricing ? 'pt-2 pb-10 md:py-12' : 'py-10 md:py-20'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`text-center ${isKingmanPricing ? 'mb-4 md:mb-8' : 'mb-6 md:mb-16'}`}>
            {!isKingmanPricing && (
              <div className={city ? 'hidden md:block' : ''}>
                <p className="text-sm font-bold uppercase tracking-wider text-green-400 mb-2">Select WAVE-enabled areas only</p>
                <h2 className="text-2xl md:text-4xl font-bold text-white mb-2 md:mb-4">
                  Airmax WAVE — Next-Generation Coverage
                </h2>
              </div>
            )}
            <p className="hidden md:block text-lg md:text-xl text-white font-semibold max-w-3xl mx-auto mb-3">
              Lightning-fast Fiber Speeds for streaming, gaming, work, and your whole home
            </p>
            <p className="hidden md:block text-white/60 max-w-3xl mx-auto mb-8">
              Get symmetrical speeds up to 1 Gig, free installation on featured plans, unlimited data, and local Arizona support — no contracts, no surprises. Pick your plan and check your address today.
            </p>
            <p className={`md:hidden text-sm text-white/55 mb-4 ${city ? 'hidden' : ''}`}>
              Swipe packages · WAVE Fiber Speeds
            </p>

            {/* Residential / Business toggle */}
            <div className="mb-4">
              <div className="inline-flex items-center bg-white/10 rounded-full p-1 border border-white/10">
                <button
                  type="button"
                  onClick={() => setCustomerType('residential')}
                  className={`px-6 py-2 rounded-full font-medium transition-all ${
                    customerType === 'residential'
                      ? 'bg-blue-600 text-white shadow'
                      : 'text-white/70 hover:bg-white/10'
                  }`}
                >
                  Residential
                </button>
                <button
                  type="button"
                  onClick={() => setCustomerType('business')}
                  className={`px-6 py-2 rounded-full font-medium transition-all ${
                    customerType === 'business'
                      ? 'bg-blue-600 text-white shadow'
                      : 'text-white/70 hover:bg-white/10'
                  }`}
                >
                  Business
                </button>
              </div>
            </div>
          </div>

          {customerType === 'business' && (
            <p className="hidden md:block text-center text-white/65 max-w-3xl mx-auto mb-8">
              Business packages include <strong className="text-white">Bizz Support</strong> — priority help from our Arizona business team when you need it.
            </p>
          )}

          {activeAudiencePlans.length > 0 ? (
            <>
              <MobilePlanCarousel
                plans={activeAudiencePlans}
                variant={customerType === 'business' ? 'business' : 'wave'}
                getDownloadSpeed={getDownloadSpeed}
                getUploadSpeed={getUploadSpeed}
                onGetConnected={handleGetConnected}
              />
              <div
                className={
                  customerType === 'business'
                    ? `hidden md:grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-5xl mx-auto ${
                        activeAudiencePlans.length === 1 ? 'max-w-md' : ''
                      }`
                    : `hidden md:grid grid-cols-1 md:grid-cols-2 gap-8 ${
                        activeAudiencePlans.length === 1
                          ? 'lg:grid-cols-1 max-w-md mx-auto'
                          : activeAudiencePlans.length > 4
                            ? 'lg:grid-cols-3 xl:grid-cols-4'
                            : 'lg:grid-cols-4'
                      }`
                }
              >
                {activeAudiencePlans.map((plan, index) =>
                  renderPlanCard(plan, index, customerType === 'business' ? 'business' : 'wave')
                )}
              </div>
            </>
          ) : (
            <p className="text-center text-white/60 max-w-xl mx-auto">
              {customerType === 'business'
                ? 'Business packages coming soon. Call '
                : 'Residential packages coming soon. Call '}
              <a href="tel:9285656707" className="text-blue-400 font-semibold hover:underline">928-565-6707</a>
              .
            </p>
          )}
        </div>
      </section>

      {/* Features Comparison - hidden on mobile */}
      <section className="hidden md:block py-20 bg-[#111827]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Compare All Features
            </h2>
            <p className="text-xl text-white/60 max-w-3xl mx-auto">
              All plans include our core features with varying speeds and capabilities.
            </p>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full bg-[#0a0f1a] rounded-lg overflow-hidden border border-white/10 shadow-lg shadow-black/30">
              <thead className="bg-white/5">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-white">Feature</th>
                  {visiblePlans.map((plan) => (
                    <th key={plan.id} className={`px-6 py-4 text-center text-sm font-semibold ${
                      plan.category === 'business' || plan.name === 'Business PRO'
                        ? 'bg-white/10 text-white border-b-4 border-slate-400'
                        : plan.category === 'fiber' || (isKingmanPricing && plan.name === 'STARTER')
                        ? 'bg-green-500/15 text-green-200 border-b-4 border-green-500' 
                        : 'bg-blue-500/15 text-blue-200 border-b-4 border-blue-500'
                    }`}>
                      {plan.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10">
                <tr>
                  <td className="px-6 py-4 text-sm text-white font-medium">Download Speed</td>
                  {visiblePlans.map((plan) => (
                    <td key={plan.id} className="px-6 py-4 text-sm text-center text-white/65">
                      {getDownloadSpeed(plan)}
                    </td>
                  ))}
                </tr>
                <tr className="bg-white/5">
                  <td className="px-6 py-4 text-sm text-white font-medium">Upload Speed</td>
                  {visiblePlans.map((plan) => (
                    <td key={plan.id} className="px-6 py-4 text-sm text-center text-white/65">
                      {getUploadSpeed(plan)}
                    </td>
                  ))}
                </tr>
                <tr>
                  <td className="px-6 py-4 text-sm text-white font-medium">Data Cap</td>
                  {visiblePlans.map((plan) => (
                    <td key={plan.id} className="px-6 py-4 text-sm text-center text-white/65">
                      Unlimited
                    </td>
                  ))}
                </tr>
                <tr className="bg-white/5">
                  <td className="px-6 py-4 text-sm text-white font-medium">Installation</td>
                  {visiblePlans.map((plan) => (
                    <td key={plan.id} className="px-6 py-4 text-sm text-center text-white/65">
                      Free
                    </td>
                  ))}
                </tr>
                <tr>
                  <td className="px-6 py-4 text-sm text-white font-medium">Premium Router <span className="text-white/45 text-xs">(Optional Add-on)</span></td>
                  {visiblePlans.map((plan) => (
                    <td key={plan.id} className="px-6 py-4 text-sm text-center text-white/65">
                      {getRouterRentalDisplay(plan)}
                    </td>
                  ))}
                </tr>
                <tr className="bg-white/5">
                  <td className="px-6 py-4 text-sm text-white font-medium">Contract</td>
                  {visiblePlans.map((plan) => (
                    <td key={plan.id} className="px-6 py-4 text-sm text-center text-white/65">
                      No Contract
                    </td>
                  ))}
                </tr>
                <tr>
                  <td className="px-6 py-4 text-sm text-white font-medium">24/7 Online Support Center</td>
                  {visiblePlans.map((plan) => (
                    <td key={plan.id} className="px-6 py-4 text-sm text-center">
                      <CheckCircleIcon className="h-5 w-5 text-green-400 mx-auto" />
                    </td>
                  ))}
                </tr>
                <tr>
                  <td className="px-6 py-4 text-sm text-white font-medium">Static IP <span className="text-white/45 text-xs">(Optional Add-on)</span></td>
                  {visiblePlans.map((plan) => (
                    <td key={plan.id} className="px-6 py-4 text-sm text-center">
                      <span className="text-white font-semibold">
                        {plan.name === 'STARTER'
                          ? 'N/A'
                          : plan.category === 'business' || plan.name === 'Business PRO'
                            ? 'Included / Available'
                            : '$5.00/month'}
                      </span>
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {localSeo && <LocalCitySeoBlock content={localSeo} />}

      {/* FAQ Section — desktop only */}
      <section className="hidden md:block py-20 bg-[#0a0f1a]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-white/60">
              Got questions? We've got answers.
            </p>
          </div>

          <div className="space-y-6">
            <div className="bg-[#111827] p-6 rounded-lg border border-white/10">
              <h3 className="text-lg font-semibold text-white mb-2">
                Is there a data cap on your plans?
              </h3>
              <p className="text-white/65">
                No, all our plans come with unlimited data. You can browse, stream, and download as much as you want without worrying about overage fees.
              </p>
            </div>

            <div className="bg-[#111827] p-6 rounded-lg border border-white/10">
              <h3 className="text-lg font-semibold text-white mb-2">
                Do I need to sign a contract?
              </h3>
              <p className="text-white/65">
                No contracts required! We believe in earning your business every month with great service, not locking you into long-term commitments.
              </p>
            </div>

            <div className="bg-[#111827] p-6 rounded-lg border border-white/10">
              <h3 className="text-lg font-semibold text-white mb-2">
                What's included in the installation?
              </h3>
              <p className="text-white/65">
                Professional installation includes mounting the antenna, running cables to your router, and ensuring everything is working perfectly. Installation is free for all new customers.
              </p>
            </div>

            <div className="bg-[#111827] p-6 rounded-lg border border-white/10">
              <h3 className="text-lg font-semibold text-white mb-2">
                Can I change my plan later?
              </h3>
              <p className="text-white/65">
                Yes! You can upgrade or downgrade your plan at any time. Changes take effect at the start of your next billing cycle.
              </p>
            </div>

            <div className="bg-[#111827] p-6 rounded-lg border border-white/10">
              <h3 className="text-lg font-semibold text-white mb-2">
                What equipment do I need?
              </h3>
              <p className="text-white/65">
                We provide all necessary equipment including the outdoor antenna and router. You can use your own router if you prefer, but our equipment is optimized for our network.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 bg-gradient-to-r from-airmax-blue to-blue-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              {city ? `Ready for better internet in ${city}?` : 'Ready to Get Started?'}
            </h2>
            <p className="text-xl text-blue-100 mb-8">
              {city
                ? `Check availability at your ${city} address and choose the perfect plan.`
                : 'Check your availability and choose the perfect plan for your needs.'}
            </p>
            <button
              type="button"
              onClick={() => {
                const first =
                  plans.find((p) =>
                    customerType === 'business'
                      ? p.category === 'business' || p.name === 'Business PRO'
                      : p.category !== 'business' && p.name !== 'Business PRO'
                  ) || plans[0];
                if (first) handleGetConnected(first);
              }}
              className="btn-primary bg-white text-airmax-blue hover:bg-gray-100 text-lg px-8 py-4 inline-flex items-center justify-center"
            >
              Get Connected
            </button>
          </motion.div>
        </div>
      </section>

      {/* Advanced Router Modal */}
      <AdvancedRouterModal isOpen={routerModalOpen} onClose={() => setRouterModalOpen(false)} />
      <PlanConnectModal
        open={!!connectPlan}
        plan={connectPlan}
        onClose={() => setConnectPlan(null)}
      />
    </div>
  );
};

export default Plans;
